/**
 * Pure, React-agnostic search helpers for the knowledge-base search (#1211).
 *
 * Upgrades the old naive whole-section substring filter to tokenized
 * multi-term matching (words can be non-adjacent and in any order), relevance
 * scoring for ranking, and highlight splitting so the renderer can wrap matched
 * runs without dangerouslySetInnerHTML. No DOM and no theme here - the renderer
 * consumes splitHighlight()'s segments. Matching is plain case-insensitive
 * substring scanning (no RegExp), so regex-special characters are literal and
 * there is no ReDoS surface.
 */

/** Split a raw query into lowercased, non-empty whitespace tokens. */
export const tokenizeQuery = (q) =>
  String(q == null ? '' : q)
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);

/**
 * True when every term appears somewhere in `text` (order-independent). An
 * empty term list matches everything (no active search).
 */
export const matchesAllTerms = (text, terms) => {
  if (!terms || !terms.length) return true;
  const haystack = String(text == null ? '' : text).toLowerCase();
  return terms.every((t) => haystack.includes(t));
};

/** Count non-overlapping occurrences of `needle` in already-lowercased `haystack`. */
const countOccurrences = (haystack, needle) => {
  if (!needle) return 0;
  let count = 0;
  let idx = haystack.indexOf(needle);
  while (idx !== -1) {
    count += 1;
    idx = haystack.indexOf(needle, idx + needle.length);
  }
  return count;
};

/** Extra weight added when a term is found in the section title (vs body copy). */
export const TITLE_WEIGHT = 5;

/**
 * Relevance score used to rank matching sections. For each term: add the number
 * of occurrences in the section's full searchable text, plus TITLE_WEIGHT when
 * the title contains it. Higher = stronger match. `haystack` is the section's
 * searchable text, `title` its heading; both are lowercased here.
 */
export const scoreSection = (haystack, title, terms) => {
  const hay = String(haystack == null ? '' : haystack).toLowerCase();
  const titleLc = String(title == null ? '' : title).toLowerCase();
  let score = 0;
  for (const term of terms || []) {
    if (!term) continue;
    score += countOccurrences(hay, term);
    if (titleLc.includes(term)) score += TITLE_WEIGHT;
  }
  return score;
};

/**
 * Split `text` into ordered segments around case-insensitive matches of any
 * term: returns [{ text, match }] covering the whole string in order. Longer
 * terms are matched first so overlapping terms (e.g. "ven" / "venmo") prefer the
 * longer hit at a position. Plain substring scan - regex-special characters in a
 * term are treated literally. With no terms (or empty text) returns a single
 * non-match segment.
 */
export const splitHighlight = (text, terms) => {
  const str = String(text == null ? '' : text);
  const cleaned = (terms || []).filter(Boolean).map((t) => t.toLowerCase());
  if (!cleaned.length || !str) return [{ text: str, match: false }];

  // Longest-first so a longer term wins over a shorter one starting at the same spot.
  cleaned.sort((a, b) => b.length - a.length);
  const lower = str.toLowerCase();

  const segments = [];
  let i = 0;
  let plainStart = 0;
  while (i < str.length) {
    let hitLen = 0;
    for (const term of cleaned) {
      if (lower.startsWith(term, i)) {
        hitLen = term.length;
        break;
      }
    }
    if (hitLen) {
      if (i > plainStart) segments.push({ text: str.slice(plainStart, i), match: false });
      segments.push({ text: str.slice(i, i + hitLen), match: true });
      i += hitLen;
      plainStart = i;
    } else {
      i += 1;
    }
  }
  if (plainStart < str.length) segments.push({ text: str.slice(plainStart), match: false });
  return segments.length ? segments : [{ text: str, match: false }];
};
